import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Sparkles } from 'lucide-react';
import { LOGO_URL, SCHOOL } from '../../lib/constants';
import { useLeadModal } from '../../lib/leadModalContext';

const Hero = () => {
  const { openLeadModal } = useLeadModal();

  return (
    <section
      id="top"
      className="relative pt-24 sm:pt-32 lg:pt-36 pb-10 sm:pb-16 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Left: Copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7"
        >
          <Badge
            variant="outline"
            className="border-[color:var(--neon-red)]/50 text-[color:var(--neon-red)] font-mono-ui uppercase tracking-[0.22em] text-[10px] glow-red-sm"
            data-testid="hero-location-badge"
          >
            <MapPin className="h-3 w-3 mr-1" /> Revolution / Montebello · Colorado Springs
          </Badge>

          <h1
            className="mt-5 sm:mt-6 font-display font-black tracking-tight text-[color:var(--neon-red)] hero-title-pulse text-[2.5rem] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[5.5rem] sm:leading-[0.95]"
            data-testid="hero-primary-title"
          >
            Adult Karate in
            <br />
            Colorado Springs, CO.
          </h1>

          <p className="mt-3 sm:mt-4 font-mono-ui uppercase tracking-[0.2em] text-[10px] sm:text-xs text-[color:var(--neon-cyan)]">
            Kempo Karate <span className="text-white/40 mx-1">•</span> Self-Defense <span className="text-white/40 mx-1">•</span> Fitness <span className="text-white/40 mx-1">•</span> Beginners Welcome
          </p>

          <h2 className="mt-4 sm:mt-5 font-display font-semibold tracking-tight text-white text-xl sm:text-3xl lg:text-4xl leading-[1.15] sm:leading-[1.1]">
            Confidence. Discipline.{' '}
            <span className="text-white/80">Real-World Self-Defense.</span>
          </h2>

          <p className="mt-4 sm:mt-5 text-white/75 text-[15px] sm:text-lg max-w-xl leading-relaxed">
            <span className="text-white">Universal Kempo Karate</span> is a family-owned martial
            arts school in Colorado Springs, Colorado, offering{' '}
            <span className="text-white">adult karate</span>,{' '}
            <span className="text-white">Kempo karate</span>, practical self-defense, fitness, and
            martial arts training for beginners and experienced students. Located near{' '}
            <span className="text-white">Montebello</span>, our Colorado Springs karate school has
            served local families and students <span className="text-white">since 1980</span>.
          </p>

          {/* Trust strip */}
          <div
            className="mt-6 flex flex-wrap items-center gap-4 text-xs text-white/70"
            data-testid="hero-trust-strip"
          >
            <span className="font-mono-ui uppercase tracking-[0.18em]">Since 1980</span>
            <span className="h-3 w-px bg-white/20" />
            <span className="font-mono-ui uppercase tracking-[0.18em]">Veteran &amp; Family-Owned</span>
            <span className="h-3 w-px bg-white/20" />
            <span className="font-mono-ui uppercase tracking-[0.18em]">Beginners Welcome</span>
          </div>

          {/* CTAs */}
          <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
            <Button
              onClick={() => openLeadModal('hero-primary')}
              className="w-full sm:w-auto bg-[color:var(--neon-red)] hover:bg-[#ff3a3a] text-white font-display uppercase tracking-[0.14em] rounded-xl px-6 py-6 text-sm glow-red-md min-h-[52px]"
              data-testid="hero-book-trial-button"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Book a Free Trial Class
            </Button>
            <a
              href={SCHOOL.directionsUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex w-full sm:w-auto items-center justify-center font-display uppercase tracking-[0.14em] rounded-xl px-6 py-4 sm:py-[22px] text-sm text-white border border-white/15 hover:bg-white/5 transition-colors min-h-[52px]"
              data-testid="hero-directions-button"
            >
              <MapPin className="h-4 w-4 mr-2" /> Get Directions
            </a>
          </div>

          <p className="mt-4 text-xs text-white/50 font-mono-ui">
            {SCHOOL.address}
          </p>
        </motion.div>

        {/* Right: Neon ring around patch */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative mx-auto w-[min(300px,68vw)] sm:w-[min(420px,80vw)] aspect-square">
            {/* Pulsing red ring */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  'conic-gradient(from 90deg, rgba(255,42,42,0.0), rgba(255,42,42,0.55), rgba(255,42,42,0.0))',
                filter: 'blur(20px)',
                opacity: 0.55,
              }}
            />
            <div className="absolute inset-4 rounded-full border border-[color:var(--neon-red)]/40 glow-red-sm" />
            <div className="absolute inset-8 rounded-full border border-white/10" />
            {/* Patch */}
            <div className="absolute inset-12 rounded-full overflow-hidden flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <img
                src={LOGO_URL}
                alt="Universal Kempo Karate Patch"
                className="w-full h-full object-contain"
                data-testid="hero-patch-image"
              />
            </div>
            {/* Tron tick marks */}
            <div className="absolute inset-0">
              {Array.from({ length: 12 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute left-1/2 top-1/2 h-[8px] w-px bg-[color:var(--neon-cyan)]/40"
                  style={{ transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-49%)` }}
                />
              ))}
            </div>
          </div>

          {/* Mini info card */}
          <div className="mt-6 mx-auto max-w-md panel rounded-2xl p-4 text-white">
            <div className="flex items-center justify-between">
              <span className="font-display uppercase tracking-[0.18em] text-[11px] text-white/70">
                Mind • Body • Spirit
              </span>
              <span className="font-mono-ui text-[11px] text-[color:var(--neon-cyan)]">
                EST. 1980
              </span>
            </div>
            <div className="mt-2 text-sm text-white/80">
              Kids, Family &amp; Adult classes. Beginners welcomed every week.
            </div>
          </div>
        </motion.div>
      </div>

      <div className="neon-divider mt-16 max-w-7xl mx-auto" />
    </section>
  );
};

export default Hero;
